import { memo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router-dom';

import { excludeKey, setErrors } from '@utils/object';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import { Button, useSnackbar } from '@new-components/elements';
import { useProfile } from '@hooks/use-profile';
import utils from 'utils';
import { useActionLog } from '@hooks/use-action-log';
import { useRGetSelectedLoan } from '@repositories/loan';
import {
  useRGetApplicationData,
  FormParam,
  useRSaveApplication,
  useRGetSavedApplication,
  SavedApplicationDataResponse,
  useRVerifyBankAccount,
} from '@repositories/new-application';
import DialogBankAccountValidation from './components/dialogs/bank-account-validation';
import DialogFailedBankAccountValidation from './components/dialogs/failed-bank-account-verification';

import ApplicationCard from '../../application-card';

import {
  SectionAccountInformation,
  SectionExpenseIncome,
  SectionPreLoan,
} from './components/sections';
import { FinancialProps } from './types';
import { defaultValues, financialSchema } from './constants';
import sentryError from 'services/sentry';

const Financial = (props: FinancialProps) => {
  const history = useHistory();
  const { setStep } = props;
  const { onOpen } = useSnackbar();
  const [showBankValidationModal, setShowBankValidationModal] = useState(false);
  const [showFailedBankValidationModal, setShowFailedBankValidationModal] =
    useState(false);

  const { data: profile } = useProfile();
  const { data: preLoanRes } = useRGetSelectedLoan({
    onError: (err) => {
      sentryError.handleSentryApiError(
        {
          response: {
            status: err.payload?.statusCode || 0,
            data: err.payload || {},
          },
        },
        null,
      );
    },
    onSuccess: (res) => {
      // log to sentry if response is not complete
      if (res.data.repayment.tenure === 0 || res.data.loan.amount === 0) {
        const sentryErrorObject = {
          response: {
            status: 200,
            data: {
              message: 'Incomplete pre-loan data',
              extraInfo: res.data,
              applicationId: profile?.application.applicationId,
            },
          },
        };
        sentryError.handleSentryApiError(sentryErrorObject, null);
      }
    },
  });
  const preLoanData = preLoanRes?.data;
  const isValidLoan =
    preLoanData &&
    preLoanData?.loan.amount > 0 &&
    preLoanData?.repayment.tenure > 0;
  const { pushEvent } = useActionLog({ page: 'app_form_financial_page' });

  const form = useForm<FormParam<'financial'>>({
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(financialSchema),
    mode: 'onChange',
  });
  const { handleSubmit, setError, reset, getValues, setValue } = form;

  const { data } = useRGetSavedApplication<
    SavedApplicationDataResponse<'financial'>
  >({
    step: 3,
    onSuccess: ({ data }) => {
      reset({ ...getValues(), ...data });
    },
  });

  const { isLoading: isLoadingGetApplicationData } = useRGetApplicationData({
    page: 3,
  });

  const { mutate: mutateSaveForm, isLoading: isSavingForm } =
    useRSaveApplication({
      onError: (err) => {
        const statusCode = err.payload?.statusCode;
        const errors = (err.payload?.errors || {}) as FormParam<'financial'>;

        switch (statusCode) {
          case 400:
            setErrors<FormParam<'financial'>>(errors, setError);
            break;

          default:
            onOpen({
              message:
                (err.payload?.errors as Array<string>)?.[0] ||
                'Opps, Something went wrong.',
              severity: 'error',
            });
            break;
        }
      },
    });

  const { mutate: verifyBankAccount, isLoading: isLoadingVerifyBankAccount } =
    useRVerifyBankAccount({
      onSuccess: () => {
        pushEvent({
          element: 'success_bank_account_verification',
        });
        setShowBankValidationModal(false);
        const fields = getValues();
        mutateSaveForm(
          {
            variables: {
              ...excludeKey<SavedApplicationDataResponse<'financial'>>(
                fields as SavedApplicationDataResponse<'financial'>,
                ['currentStep', 'referralCode'],
              ),
              step: 3,
            },
          },
          {
            onSuccess: () => {
              setValue('bankAccountNumberVerified', true);
              setShowBankValidationModal(false);
              setStep((prev) => prev + 1);
            },
          },
        );
      },
      onError: (err) => {
        const payload = (err.payload || {
          statusCode: 400,
          errors: [],
        }) as {
          statusCode: number;
          errors: string[];
        };
        const statusCode = payload.statusCode;
        setValue('bankAccountNumberVerified', false);
        switch (statusCode) {
          case 400:
            setShowBankValidationModal(false);
            setShowFailedBankValidationModal(true);
            setTimeout(() => {
              setError('bankAccountNumber', {
                message: payload.errors[0],
              });
            }, 100);
            pushEvent({
              element: 'failed_bank_account_verification',
            });
            break;

          default:
            pushEvent({
              element: 'error_bank_account_verification',
            });
            break;
        }
      },
    });

  const handleVerify = () => {
    verifyBankAccount({
      variables: {
        bankAccountNumber: getValues('bankAccountNumber') || '',
        bankName: getValues('bankName') || '',
      },
    });
  };

  async function onSubmit() {
    if (isValidLoan) {
      setShowBankValidationModal(true);
    } else {
      // save progress and redirect to loan offer page
      if (preLoanData?.loanOffer) {
        const fields = getValues();
        mutateSaveForm(
          {
            variables: {
              ...excludeKey<SavedApplicationDataResponse<'financial'>>(
                fields as SavedApplicationDataResponse<'financial'>,
                ['currentStep', 'referralCode'],
              ),
              step: 3,
            },
          },
          {
            onSuccess: () => {
              utils.store.set('preAuthToken', profile?.grabToken || '');
              localStorage.setItem('origin', 'app_form');
              localStorage.setItem(
                'pre_loan_offer',
                JSON.stringify({
                  ...preLoanData?.loanOffer,
                  interestRate: preLoanData?.repayment?.interestRate,
                  upfrontFee: preLoanData?.loan?.fee,
                  loanAmount: preLoanData?.loan.amount,
                  repaymentTenure: preLoanData?.repayment.tenure,
                }),
              );
              history.push('/offer');
            },
          },
        );
      }
    }
  }

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <Helmet>
        <title>JULO - Form Informasi Keuangan</title>
      </Helmet>
      <form
        id='financial-form'
        onSubmit={handleSubmit(onSubmit)}
        className='family-information'
      >
        <SectionExpenseIncome />
        <SectionAccountInformation
          defaultIsVerifiedAccount={(data?.data.currentStep ?? 1) > 3}
        />

        <SectionPreLoan />

        <ApplicationCard>
          <Button
            type='submit'
            disabled={isSavingForm || isLoadingGetApplicationData}
            fullWidth
          >
            Lanjutkan
          </Button>
        </ApplicationCard>

        <DialogBankAccountValidation
          isOpen={showBankValidationModal}
          onCancel={() => {
            setValue('bankAccountNumberVerified', false);
            setShowBankValidationModal(false);
          }}
          onVerify={handleVerify}
          isLoading={isLoadingVerifyBankAccount}
        />

        <DialogFailedBankAccountValidation
          isOpen={showFailedBankValidationModal}
          onClose={() => setShowFailedBankValidationModal(false)}
        />
      </form>
    </FormProvider>
  );
};

export default memo(Financial);
